import type { ComponentProps } from 'react';
import { StyleSheet, type UnistylesVariants } from 'react-native-unistyles';
import { useMutation } from 'urql';
import { v4 } from 'uuid';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import useAuthStore from '@/stores/useAuthStore';
import hapticsOnPress from '@/utils/hapticsOnPress';
import Pressable from '../Unistyles/Pressable';
import { ChatMessageEmojiInputFragment } from './ChatMessageEmojiInput.utils';

function Count({ children }: { children: React.ReactNode }) {
  return <Text size="md">{children}</Text>;
}

function Emoji({
  children,
  size,
}: {
  children: React.ReactNode;
  size: ComponentProps<typeof Text>['size'];
}) {
  return <Text size={size}>{children}</Text>;
}

export const ChatMessageEmojiInputInsertReaction = graphql(`
  mutation ChatMessageEmojiInputInsertReaction(
    $messageId: uuid!
    $content: String!
    $reactionId: uuid!
  ) {
    insert_message_reaction_one(
      object: { id: $reactionId, message_id: $messageId, content: $content }
    ) {
      id
      message {
        ...ChatMessageEmojiInput
      }
    }
  }
`, [ChatMessageEmojiInputFragment]);

export const ChatMessageEmojiInputDeleteReaction = graphql(`
  mutation ChatMessageEmojiInputDeleteReaction($messageReactionId: uuid!) {
    delete_message_reaction_by_pk(id: $messageReactionId) {
      id
      message {
        ...ChatMessageEmojiInput
      }
    }
  }
`, [ChatMessageEmojiInputFragment]);

type ChatMessageEmojiInputProps = UnistylesVariants<typeof style> & {
  emoji: string;
  showCount?: boolean;
  fragment: FragmentOf<typeof ChatMessageEmojiInputFragment>;
  size?: ComponentProps<typeof Text>['size'];
  onSubmit?: () => void;
};

export default function ChatMessageEmojiInput({
  emoji,
  showCount = true,
  fragment,
  variant,
  size = 'lg',
  onSubmit,
}: ChatMessageEmojiInputProps) {
  style.useVariants({
    variant,
  });

  const [{ fetching: isInserting }, insertReaction] = useMutation(
    ChatMessageEmojiInputInsertReaction,
  );

  const [{ fetching: isDeleting }, deleteReaction] = useMutation(
    ChatMessageEmojiInputDeleteReaction,
  );

  const isFetching = isInserting || isDeleting;
  const message = readFragment(ChatMessageEmojiInputFragment, fragment);

  const didYouReact = message.your_reactions?.find(
    (reaction: { content: string }) => reaction.content === emoji,
  );

  const count =
    message.reaction_count?.find((reactionCountItem) =>
      Object.hasOwn(reactionCountItem, emoji),
    )?.[emoji] || 0;

  const onPress = async () => {
    const personId = useAuthStore.getState().uid;

    if (!personId) {
      return;
    }

    onSubmit?.();

    if (didYouReact) {
      const messageReactionId = message.your_reactions?.find(
        (reaction: { content: string }) => reaction.content === emoji,
      )?.id;

      if (!messageReactionId) {
        return;
      }

      await deleteReaction({
        messageReactionId: messageReactionId,
      });

      return;
    }

    await insertReaction({
      messageId: message.id,
      content: emoji,
      reactionId: v4(),
    });
  };

  return (
    <Pressable
      accessibilityLabel={`React ${emoji}`}
      onPress={hapticsOnPress(onPress)}
      style={style.item(!!didYouReact)}
      disabled={isFetching}
    >
      {showCount ? <Count>{count}</Count> : null}
      <Emoji size={size}>{emoji}</Emoji>
    </Pressable>
  );
}

const style = StyleSheet.create((theme) => ({
  item: (didYouReact: boolean) => ({
    paddingVertical: theme.space('sm'),
    paddingHorizontal: theme.space('md'),
    borderRadius: theme.borderRadius.xl,
    borderColor: didYouReact ? theme.colors.info : theme.colors.borderMuted,
    borderWidth: theme.size(0.5),
    backgroundColor: 'transparent',
    alignItems: 'center',
    gap: theme.space('sm'),
    flexDirection: 'row',
    justifyContent: 'center',
    variants: {
      variant: {
        default: {
          backgroundColor: didYouReact
            ? theme.colors.primaryMuted
            : 'transparent',
          _web: {
            _hover: {
              backgroundColor: didYouReact
                ? theme.colors.primaryHover
                : theme.colors.secondaryHover,
            },
          },
        },
        borderless: {
          backgroundColor: didYouReact
            ? theme.colors.primaryMuted
            : 'transparent',
          borderWidth: 0,
          borderRadius: theme.borderRadius.none,
          _web: {
            _hover: {
              backgroundColor: didYouReact
                ? theme.colors.primaryHover
                : theme.colors.secondaryHover,
            },
          },
        },
      },
    },
  }),
}));
